/**
 * MobileLayout — wraps mobile page content with the bottom tab bar.
 *
 * Manages a simple toast for the disabled Projection tab.
 */

import { useState, useCallback, useRef, useEffect, type ReactNode } from 'react';
import { MobileNavBar } from '../MobileNavBar/MobileNavBar';
import './MobileLayout.css';

interface MobileLayoutProps {
  children: ReactNode;
}

export function MobileLayout({ children }: Readonly<MobileLayoutProps>) {
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  // Clean up toast timer on unmount
  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  // Reactive: re-check sessionStorage whenever it changes.
  // The native `storage` event only fires for cross-tab writes, so we also
  // poll every 2 s to pick up same-tab updates (e.g. after wizard submission).
  const [hasProjection, setHasProjection] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return sessionStorage.getItem('plan-pair') !== null;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const check = () => {
      try {
        setHasProjection(sessionStorage.getItem('plan-pair') !== null);
      } catch {
        setHasProjection(false);
      }
    };
    window.addEventListener('storage', check);
    const interval = setInterval(check, 2_000);
    return () => {
      window.removeEventListener('storage', check);
      clearInterval(interval);
    };
  }, []);

  const showResultsToast = useCallback(() => {
    clearTimeout(timerRef.current);
    setToastMessage('Complete your plan first to see your projection.');
    timerRef.current = setTimeout(() => setToastMessage(null), 3000);
  }, []);

  return (
    <div className="mobile-layout">
      <main className="mobile-layout__content">
        {children}
      </main>
      <MobileNavBar
        hasProjection={hasProjection}
        onResultsDisabledTap={showResultsToast}
      />
      {toastMessage && (
        <div className="mobile-layout__toast" role="status" aria-live="polite">
          {toastMessage}
        </div>
      )}
    </div>
  );
}
